import {SplitViewEntries} from '@webmate-web-core/shared/components/split-view/interfaces/split-view.interface';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnChanges, SimpleChanges} from '@angular/core';

import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {SharedEntryListComponent} from '@webmate-web-core/shared/components/split-view/components/entry-lists/shared-entry-list.component';
import {CheckboxModule} from 'primeng/checkbox';
import {FormsModule} from '@angular/forms';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {EntryActionConfig, GroupingConfig} from '@webmate-web-core/shared/components/split-view/components/entry-lists/entry-list-util';
import {TooltipModule} from 'primeng/tooltip';
import {NgClass, NgTemplateOutlet} from '@angular/common';

@Component({
    selector: 'wm-split-view-simple-entry-list',
    templateUrl: 'simple-entry-list.component.html',
    styleUrls: ['simple-entry-list.component.less'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CheckboxModule,
        FormsModule,
        FontAwesomeModule,
        TooltipModule,
        NgTemplateOutlet,
        NgClass
    ],
    providers: [{provide: SplitViewEntries, useExisting: forwardRef(() => SimpleEntryListComponent)}]
})
// First generic type is the type of the entries.
// Second, optional, generic type is the type of the groups if used. See GroupingConfig interface for details
export class SimpleEntryListComponent<E, G extends string = any> extends SharedEntryListComponent<E> implements OnChanges {

    override set entities(e: E[]) {
        super.entities = e;
        if (this.groupingConfig) {
            this.handleEntries(e);
        }
        this.cdRef.markForCheck();
    }

    override get entities() {
        return super.entities;
    }

    @Input() displayKey: keyof E | ((e: E) => string) = 'id' as keyof E;

    getDisplayString(e: E) {
        if (this.displayKey instanceof Function) {
            return this.displayKey(e);
        } else {
            return e[this.displayKey];
        }
    }

    @Input() entryActionConfig?: EntryActionConfig<E>;

    // Set groupingConfig to enable grouping of entries
    @Input() groupingConfig?: GroupingConfig<G, E>;
    groupMappings: Map<G, E[]> = new Map<G, E[]>();

    constructor(protected cdRef: ChangeDetectorRef, protected notificationService: WmNotificationService) {
        super(cdRef, notificationService);
    }

    ngOnChanges(changes: SimpleChanges) {
        // Check if the grouping config has changed, and update groups accordingly
        if (changes['groupingConfig'] && !changes['groupingConfig'].isFirstChange()) {
            if (changes['groupingConfig'].currentValue !== undefined) {
                this.handleEntries(this.entities);
                this.cdRef.markForCheck();
            }
        }
    }

    getGroups() {
        if (!this.groupingConfig) {
            throw new Error('Must provide groupingConfig when trying to access groups');
        }
        if (this.groupingConfig.groupSortingFunction) {
            return [...this.groupMappings.keys()].sort(this.groupingConfig.groupSortingFunction);
        } else {
            return this.groupMappings.keys();
        }
    }

    private handleEntries(e: E[]) {
        this.groupMappings = new Map<G, E[]>();
        e.forEach(entry => {
            const group = this.groupingConfig!.entryToGroup(entry);
            if (!this.groupMappings.get(group)) {
                this.groupMappings = this.groupMappings.set(group, []);
            }
            this.groupMappings.get(group)!.push(entry);
        });

        if (this.groupingConfig!.entrySortingFunction) {
            this.groupMappings.forEach((value, key) => {
                this.groupMappings.set(key, value.toSorted(this.groupingConfig!.entrySortingFunction))
            });
        }
    }

    handleClick(e: E) {
        this.entryActionConfig?.callback(e);
    }

    getIcon(e: E) {
        if (this.entryActionConfig!.altIcon) {
            return this.entryActionConfig!.altIcon.condition(e) ? this.entryActionConfig!.icon : this.entryActionConfig!.altIcon.icon;
        } else {
            return this.entryActionConfig!.icon;
        }
    }

    getIconTooltip(e: E) {
        if (this.entryActionConfig!.altIcon) {
            return this.entryActionConfig!.altIcon.condition(e) ? this.entryActionConfig!.iconTooltip : this.entryActionConfig!.altIcon.iconTooltip;
        } else {
            return this.entryActionConfig!.iconTooltip;
        }
    }

    getOnlyHoverFlag(e: E) {
        if (this.entryActionConfig?.hoverOnly instanceof Function) {
            return this.entryActionConfig?.hoverOnly(e);
        } else {
            return this.entryActionConfig?.hoverOnly;
        }
    }

    handleGroupClick(g: G) {
        this.groupingConfig?.groupActionConfig?.callback(g);
    }

    getGroupIcon(g: G) {
        if (this.groupingConfig?.groupActionConfig?.altIcon) {
            return this.groupingConfig?.groupActionConfig?.altIcon.condition(g) ? this.groupingConfig?.groupActionConfig?.icon : this.groupingConfig?.groupActionConfig?.altIcon.icon;
        } else {
            return this.groupingConfig?.groupActionConfig?.icon;
        }
    }

    getGroupIconTooltip(g: G) {
        if (this.groupingConfig?.groupActionConfig?.altIcon) {
            return this.groupingConfig?.groupActionConfig?.altIcon.condition(g) ? this.groupingConfig?.groupActionConfig?.iconTooltip : this.groupingConfig?.groupActionConfig?.altIcon.iconTooltip;
        } else {
            return this.groupingConfig?.groupActionConfig?.iconTooltip;
        }
    }

    getGroupOnlyHoverFlag(g: G) {
        if (this.groupingConfig?.groupActionConfig?.hoverOnly instanceof Function) {
            return this.groupingConfig?.groupActionConfig?.hoverOnly(g);
        } else {
            return this.groupingConfig?.groupActionConfig?.hoverOnly;
        }
    }

}
